import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,

  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  Chip,
  Snackbar,
  Alert,
  Box
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  PlayArrow as PlayArrowIcon,
  Queue as QueueIcon,
  PlaylistAdd as PlaylistAddIcon,
  Create as CreateIcon,
  Download as DownloadIcon,
  ShoppingCart as ShoppingCartIcon,
  Bookmark as BookmarkIcon,
  Share as ShareIcon,
  PostAdd as PostAddIcon,
  RateReview as RateReviewIcon,
  OfflinePin as OfflinePinIcon,
  OfflinePinOutlined as OfflinePinOutlinedIcon
} from '@mui/icons-material';
import ShareToFeedDialog from '../Post/ShareToFeedDialog';
import { ApiRoutes, AppRoutes, apiBaseURL } from '../../constants/routes';
import { useLibrary } from '../../hooks/useLibrary';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CircularProgress } from '@mui/material';
import DepositDialog from '../PaymentComponents/DepositDialog';
import StripeProvider from '../PaymentComponents/StripeProvider';
import { useAuthProvider } from '../../utils/auth';
import { useShareLink } from '../../hooks/useShareReferral';
import { useActCreditBalance } from '../../hooks/useActData';
import { useCanReview } from '../../hooks/useCanReview';
import { curationLabelFields, userPlaylistsUrl } from '../../utils/curation';
import ReviewDialog from '../MusicReview/ReviewDialog';
import { useNavigate } from 'react-router-dom';
import { useOfflineTrack } from '../../hooks/useOfflineTrack';
import SaveFileDialog from '../SaveFileDialog';

const TrackActionsDropdown = ({ track, onPlayNext, onAddToQueue, onAddToPlaylist, updatePlaylists, albumId, albumName, albumTracks }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [playlistDialogOpen, setPlaylistDialogOpen] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [createPlaylistDialogOpen, setCreatePlaylistDialogOpen] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [playlists, setPlaylists] = useState([]);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [addCreditDialogOpen, setAddCreditDialogOpen] = useState(false);
  const [insufficientCreditDialogOpen, setInsufficientCreditDialogOpen] = useState(false);
  
  const queryClient = useQueryClient();
  const { isActListeningMode, currentActId, isAuthed, curatingLabel } = useAuthProvider();
  const tagShareLink = useShareLink();

  // The track's own link (this used to share whatever page the menu was
  // opened on - an album, a playlist, search results)
  const getTrackShareUrl = () => {
    if (!track?.id) return window.location.href;
    if (track.is_private && track.private_link_token) {
      return `${window.location.origin}/private/track/${track.id}/${track.private_link_token}`;
    }
    return tagShareLink(`${window.location.origin}${AppRoutes.SinglePage.replace(':slug', track.slug || track.id)}`);
  };
  const { toggleLibraryItem, getLibraryStatus } = useLibrary();
  
  // Check if this is an album purchase
  const isAlbumPurchase = !!albumId && albumTracks && albumTracks.length > 0;
  const trackCount = isAlbumPurchase ? albumTracks.length : 1;
  const purchasePrice = isAlbumPurchase ? trackCount * 2.00 : 2.00;
  
  // Check if track is saved
  const { data: libraryStatus } = useQuery(
    ['library-status', 'track', track?.id],
    () => getLibraryStatus('track', track?.id),
    {
      enabled: !!track?.id,
      staleTime: 30000,
    }
  );
  
  const isSaved = libraryStatus?.is_saved || false;
  
  // Check if track is already purchased
  const { data: downloadStatus } = useQuery(
    ['track-download-status', track?.id],
    async () => {
      if (!track?.id) return null;
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      const response = await fetch(`${apiBaseURL}/music/track/${track.id}/download-status/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      
      if (!response.ok) return null;
      return response.json();
    },
    {
      enabled: !!track?.id,
      staleTime: 30000,
    }
  );
  
  const isPurchased = downloadStatus?.is_downloaded && downloadStatus?.is_permanent_purchase;
  const offline = useOfflineTrack(track);
  const [saveFileOpen, setSaveFileOpen] = useState(false);
  
  // Fetch user credit balance (or act credit balance if in act listening mode)
  const { data: creditStatus, refetch: refetchCredit } = useQuery(
    ['user-credit-status'],
    async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${apiBaseURL}/payments/credit-status/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      
      if (!response.ok) {
        // If credit account doesn't exist, return default values
        if (response.status === 404 || response.status === 400) {
          return { credit_balance: 0.0 };
        }
        throw new Error('Failed to fetch credit status');
      }
      
      return response.json();
    },
    {
      retry: false,
      staleTime: 30000, // Cache for 30 seconds
      enabled: isAuthed && !isActListeningMode, // Signed-in users only, and not in act mode
    }
  );
  
  // Fetch act credit balance if in act listening mode
  const { data: actCreditBalance, refetch: refetchActCredit } = useActCreditBalance(currentActId, {
    enabled: isActListeningMode && !!currentActId,
  });
  
  const creditBalance = isActListeningMode ? (actCreditBalance || 0.0) : (creditStatus?.credit_balance || 0.0);
  const hasSufficientCredit = creditBalance >= purchasePrice;
  
  // Refetch function that works for both user and act
  const refetchCreditBalance = async () => {
    if (isActListeningMode) {
      await refetchActCredit();
    } else {
      await refetchCredit();
    }
  };
  
  // Album purchase mutation
  const albumPurchaseMutation = useMutation(
    async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const requestBody = {};
      if (isActListeningMode && currentActId) {
        requestBody.act_id = currentActId;
      }
      
      const response = await fetch(`${apiBaseURL}/music/album/${albumId}/purchase/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Purchase failed');
      }
      
      return response.json();
    },
    {
      onSuccess: async (data) => {
        setSnackbar({ 
          open: true, 
          message: `Successfully purchased ${isAlbumPurchase ? 'album' : 'track'}!`, 
          severity: 'success' 
        });
        setPurchaseDialogOpen(false);
        // Invalidate credit balance queries
        if (isActListeningMode) {
          await queryClient.invalidateQueries(['actCreditBalance', currentActId]);
          await queryClient.refetchQueries(['actCreditBalance', currentActId]);
        } else {
          await queryClient.invalidateQueries(['user-credit-status']);
          await queryClient.refetchQueries(['user-credit-status']);
        }
        // Invalidate library and downloads queries to show purchased tracks
        await queryClient.invalidateQueries(['user-library']);
        await queryClient.invalidateQueries(['userDownloads']);
        await queryClient.invalidateQueries(['track-download-status', track?.id]);
        // Invalidate credit transactions to show purchase in charges
        await queryClient.invalidateQueries(['credit-transactions']);
        // Note: Album purchases don't trigger individual downloads, user can download from Downloads page
      },
      onError: (error) => {
        const errorMessage = error.message || 'Purchase failed. Please try again.';
        
        // Check if error is about insufficient credit or missing credit account
        if (errorMessage.includes('Credit account not found') || 
            errorMessage.includes('Insufficient credit') ||
            errorMessage.includes('credit')) {
          setPurchaseDialogOpen(false);
          setInsufficientCreditDialogOpen(true);
        } else {
          setSnackbar({ 
            open: true, 
            message: errorMessage, 
            severity: 'error' 
          });
          setPurchaseDialogOpen(false);
        }
      },
    }
  );
  
  // Track purchase mutation (using DownloadButton's logic)
  const trackPurchaseMutation = useMutation(
    async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const requestBody = {
        download_type: 'permanent_purchase'
      };
      if (isActListeningMode && currentActId) {
        requestBody.act_id = currentActId;
      }
      
      const response = await fetch(`${apiBaseURL}/music/track/${track.id}/download/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Purchase failed');
      }
      
      return response.json();
    },
    {
      onSuccess: async (data) => {
        // Trigger download if download_url is provided
        if (data.download_url) {
          try {
            const link = document.createElement('a');
            link.href = data.download_url;
            // Use file_extension from response if available, otherwise try to extract from URL
            let extension = 'mp3'; // default
            if (data.file_extension) {
              extension = data.file_extension;
              console.log('Using file_extension from response:', extension);
            } else {
              const urlExtension = data.download_url.split('.').pop().split('?')[0].toLowerCase();
              if (['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg'].includes(urlExtension)) {
                extension = urlExtension;
                console.log('Using extension from URL:', extension);
              } else {
                console.log('No extension found, using default mp3. URL:', data.download_url, 'Response data:', data);
              }
            }
            link.download = `${track.name || 'track'}.${extension}`;
            console.log('Downloading file as:', link.download);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } catch (error) {
            console.error('Error triggering download:', error);
          }
        }
        
        setSnackbar({ 
          open: true, 
          message: `Successfully purchased track! ${data.download_url ? 'Download started.' : ''}`, 
          severity: 'success' 
        });
        setPurchaseDialogOpen(false);
        // Invalidate credit balance queries
        if (isActListeningMode) {
          await queryClient.invalidateQueries(['actCreditBalance', currentActId]);
          await queryClient.refetchQueries(['actCreditBalance', currentActId]);
        } else {
          await queryClient.invalidateQueries(['user-credit-status']);
          await queryClient.refetchQueries(['user-credit-status']);
        }
        // Invalidate library and downloads queries to show purchased tracks
        await queryClient.invalidateQueries(['user-library']);
        await queryClient.invalidateQueries(['userDownloads']);
        await queryClient.invalidateQueries(['track-download-status', track?.id]);
        await queryClient.refetchQueries(['track-download-status', track?.id]);
        // Invalidate credit transactions to show purchase in charges
        await queryClient.invalidateQueries(['credit-transactions']);
      },
      onError: (error) => {
        const errorMessage = error.message || 'Purchase failed. Please try again.';
        
        // Check if error is about insufficient credit or missing credit account
        if (errorMessage.includes('Credit account not found') || 
            errorMessage.includes('Insufficient credit') ||
            errorMessage.includes('credit')) {
          setPurchaseDialogOpen(false);
          setInsufficientCreditDialogOpen(true);
        } else {
          setSnackbar({ 
            open: true, 
            message: errorMessage, 
            severity: 'error' 
          });
          setPurchaseDialogOpen(false);
        }
      },
    }
  );
  
  const handlePurchaseConfirm = () => {
    if (isAlbumPurchase) {
      albumPurchaseMutation.mutate();
    } else {
      trackPurchaseMutation.mutate();
    }
  };

  // Load playlists from backend API
  const loadPlaylists = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch(userPlaylistsUrl(curatingLabel), {
          headers: {
            'Authorization': `Token ${token}`
          }
        });
        
        if (response.ok) {
          const backendPlaylists = await response.json();
          setPlaylists(backendPlaylists);
        } else {
          console.error('TrackActionsDropdown - Failed to fetch playlists:', response.status);
          setPlaylists([]);
        }
      } else {
        // Logged out - no playlists to offer
        setPlaylists([]);
      }
    } catch (error) {
      console.error('TrackActionsDropdown - Error loading playlists:', error);
      setPlaylists([]);
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
    // Fetched on open rather than on mount: every track row renders one of
    // these menus, so loading on mount sent one playlists request per row.
    loadPlaylists();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Reviews target the album when this is an album's menu, else the track
  const reviewContentType = albumId ? 'album' : 'track';
  const reviewContentId = albumId || track?.id;
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthed: canAttemptReview, canReview, reason: cannotReviewReason } = useCanReview(
    reviewContentType, reviewContentId, { enabled: Boolean(menuAnchorEl) }
  );

  const handleWriteReview = () => {
    handleMenuClose();
    if (canAttemptReview) {
      setReviewDialogOpen(true);
    } else {
      navigate(AppRoutes.LoginPage);
    }
  };

  const handlePlayNext = () => {
    if (onPlayNext) onPlayNext(track);
    handleMenuClose();
  };

  const handleAddToQueue = () => {
    if (onAddToQueue) onAddToQueue(track);
    handleMenuClose();
  };

  const handleAddToPlaylist = () => {
    setPlaylistDialogOpen(true);
    handleMenuClose();
  };

  const handleCreatePlaylist = () => {
    setCreatePlaylistDialogOpen(true);
    setPlaylistDialogOpen(false);
  };

  const handleCreatePlaylistSubmit = async () => {
    if (!newPlaylistName.trim() || !track) return;
    
    const playlistName = newPlaylistName.trim();
    setNewPlaylistName('');
    setCreatePlaylistDialogOpen(false);
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch(ApiRoutes.PlaylistCreate, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          },
          body: JSON.stringify({
            name: playlistName,
            description: '',
            is_public: true,
            track_ids: [track.id],
            ...curationLabelFields(curatingLabel),
          })
        });
        
        if (response.ok) {
          // Reload playlists from backend
          loadPlaylists();
          
          // Add to playlist callback
          if (onAddToPlaylist) onAddToPlaylist(track);
          
          setSnackbar({ 
            open: true, 
            message: `Created playlist "${playlistName}" with "${track.name || 'Unknown Track'}"`, 
            severity: 'success' 
          });
        } else {
          console.error('TrackActionsDropdown - Failed to create playlist:', response.status);
          setSnackbar({ 
            open: true, 
            message: 'Failed to create playlist. Please try again.', 
            severity: 'error' 
          });
        }
      }
    } catch (error) {
      console.error('TrackActionsDropdown - Error creating playlist:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error creating playlist. Please try again.', 
        severity: 'error' 
      });
    }
  };

  const handleAddToExistingPlaylist = async (playlist) => {
    if (!track) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch(`${ApiRoutes.PlaylistDetail}${playlist.id}/add-track/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          },
          body: JSON.stringify({
            track_id: track.id
          })
        });
        
        if (response.ok) {
          // Reload playlists from backend
          loadPlaylists();
          
          // Add to playlist callback
          if (onAddToPlaylist) onAddToPlaylist(track);
          
          setPlaylistDialogOpen(false);
          setSnackbar({ 
            open: true, 
            message: `Added "${track.name || 'Unknown Track'}" to "${playlist.name}"`, 
            severity: 'success' 
          });
        } else {
          console.error('TrackActionsDropdown - Failed to add track to playlist:', response.status);
          setSnackbar({ 
            open: true, 
            message: 'Failed to add track to playlist. Please try again.', 
            severity: 'error' 
          });
        }
      }
    } catch (error) {
      console.error('TrackActionsDropdown - Error adding track to playlist:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error adding track to playlist. Please try again.', 
        severity: 'error' 
      });
    }
  };

  const handleClosePlaylistDialog = () => {
    setPlaylistDialogOpen(false);
  };

  const handleCloseCreateDialog = () => {
    setCreatePlaylistDialogOpen(false);
    setNewPlaylistName('');
  };

  const handleShareClick = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: track?.name || 'Track',
          text: `Check out "${track?.name || 'this track'}" on Tuneswell`,
          url: getTrackShareUrl()
        });
        // Share was successful
      } catch (error) {
        // Handle share cancellation or other errors
        if (error.name === 'AbortError') {
          // User cancelled the share dialog - this is normal, don't show error
          console.log('Share cancelled by user');
        } else {
          // Other share errors - fallback to clipboard
          console.error('Share failed:', error);
          await fallbackToClipboard();
        }
      }
    } else {
      // Fallback to copying URL to clipboard
      await fallbackToClipboard();
    }
    handleMenuClose();
  };

  const fallbackToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getTrackShareUrl());
      setSnackbar({
        open: true,
        message: 'Track link copied to clipboard',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setSnackbar({
        open: true,
        message: 'Unable to share or copy link',
        severity: 'error'
      });
    }
  };

  // Don't render if no track is provided
  if (!track) {
    return null;
  }

  return (
    <>
      {/* Three-dot menu button */}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{ 
          opacity: 0.7,
          '&:hover': { opacity: 1 }
        }}
      >
        <MoreVertIcon />
      </IconButton>

      {/* Main actions menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handlePlayNext}>
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Play Next</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleAddToQueue}>
          <ListItemIcon>
            <QueueIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add to Queue</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleAddToPlaylist}>
          <ListItemIcon>
            <PlaylistAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add to Playlist</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleShareClick}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share Track</ListItemText>
        </MenuItem>

        {isAuthed && (
          <MenuItem onClick={() => { setPostDialogOpen(true); handleMenuClose(); }}>
            <ListItemIcon>
              <PostAddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Post to Feed</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleWriteReview} disabled={canAttemptReview && !canReview}>
          <ListItemIcon>
            <RateReviewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={albumId ? 'Review Album' : 'Write a Review'}
            secondary={canAttemptReview && !canReview ? cannotReviewReason : null}
          />
        </MenuItem>
        
        <Divider />
        
        <MenuItem
          onClick={async (e) => {
            e.stopPropagation();
            handleMenuClose();
            try {
              await toggleLibraryItem('track', track?.id);
            } catch (error) {
              console.error('Error toggling save:', error);
            }
          }}
        >
          <ListItemIcon>
            {isSaved ? (
              <BookmarkIcon fontSize="small" sx={{ color: 'primary.main' }} />
            ) : (
              <BookmarkIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>{isSaved ? 'Remove from Library' : 'Save to Library'}</ListItemText>
        </MenuItem>
        
        {/* Any music can be kept in the app to play offline; music you
            haven't bought is charged when the device reconnects */}
        {offline.supported && (
          <MenuItem
            disabled={offline.saving}
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClose();
              if (offline.saved) offline.remove();
              else offline.save();
            }}
          >
            <ListItemIcon>
              {offline.saved ? <OfflinePinIcon fontSize="small" /> : <OfflinePinOutlinedIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{offline.saved ? 'Remove offline copy' : 'Keep for offline listening'}</ListItemText>
          </MenuItem>
        )}

        {/* Save the file itself - for buyers */}
        {isPurchased && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClose();
              setSaveFileOpen(true);
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save file</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem 
          onClick={async () => {
            handleMenuClose();
            
            // Don't show purchase dialog if already purchased
            if (isPurchased) {
              setSnackbar({
                open: true,
                message: 'You already own this track!',
                severity: 'info'
              });
              return;
            }
            // Check credit balance before showing purchase dialog
            await refetchCreditBalance();
            
            if (!hasSufficientCredit) {
              setInsufficientCreditDialogOpen(true);
            } else {
              setPurchaseDialogOpen(true);
            }
          }}
          disabled={isPurchased}
        >
          <ListItemIcon>
            <ShoppingCartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {isPurchased 
              ? (isAlbumPurchase ? 'Album Already Purchased' : 'Already Purchased')
              : (isAlbumPurchase ? `Purchase Album (${trackCount} tracks)` : 'Purchase')
            }
          </ListItemText>
        </MenuItem>
        
        {/* Download Again option for purchased tracks */}
        {isPurchased && (
          <MenuItem
            onClick={async () => {
              handleMenuClose();
              try {
                const token = localStorage.getItem('token');
                if (!token) {
                  throw new Error('No authentication token found');
                }
                
                const requestBody = {
                  download_type: 'permanent_purchase'
                };
                if (isActListeningMode && currentActId) {
                  requestBody.act_id = currentActId;
                }
                
                const response = await fetch(`${apiBaseURL}/music/track/${track.id}/download/`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(requestBody),
                });
                
                if (!response.ok) {
                  const errorData = await response.json();
                  throw new Error(errorData.error || 'Download failed');
                }
                
                const data = await response.json();
                
                // Trigger download if download_url is provided
                if (data.download_url) {
                  try {
                    const link = document.createElement('a');
                    link.href = data.download_url;
                    // Use file_extension from response if available, otherwise try to extract from URL
                    let extension = 'mp3'; // default
                    if (data.file_extension) {
                      extension = data.file_extension;
                      console.log('Using file_extension from response:', extension);
                    } else {
                      const urlExtension = data.download_url.split('.').pop().split('?')[0].toLowerCase();
                      if (['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg'].includes(urlExtension)) {
                        extension = urlExtension;
                        console.log('Using extension from URL:', extension);
                      } else {
                        console.log('No extension found, using default mp3. URL:', data.download_url, 'Response data:', data);
                      }
                    }
                    link.download = `${track.name || 'track'}.${extension}`;
                    console.log('Downloading file as:', link.download);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  } catch (error) {
                    console.error('Error triggering download:', error);
                  }
                }
                
                setSnackbar({
                  open: true,
                  message: 'Download started!',
                  severity: 'success'
                });
                
                // Refresh download status
                await queryClient.invalidateQueries(['track-download-status', track?.id]);
              } catch (error) {
                setSnackbar({
                  open: true,
                  message: error.message || 'Failed to download track',
                  severity: 'error'
                });
              }
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Again</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ShareToFeedDialog
        open={postDialogOpen}
        onClose={() => setPostDialogOpen(false)}
        music={track && {
          type: 'track',
          id: track.id,
          name: track.name,
          slug: track.slug,
          act_name: track.act_name || track.primary_act_name || '',
          image: track.image,
        }}
      />

      <ReviewDialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        contentType={reviewContentType}
        contentId={reviewContentId}
      />

      {/* Playlist selection dialog */}
      <Dialog 
        open={playlistDialogOpen} 
        onClose={handleClosePlaylistDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add to Playlist</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a playlist to add "{track.name}" to, or create a new one.
          </Typography>
          
          {/* Create new playlist option */}
          <ListItemButton 
            onClick={handleCreatePlaylist}
            sx={{ 
              border: '1px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              mb: 2
            }}
          >
            <ListItemIcon>
              <CreateIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Create New Playlist"
              secondary="Start a new playlist with this track"
            />
          </ListItemButton>

          <Divider sx={{ my: 2 }} />

          {/* Existing playlists */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Your Playlists:
          </Typography>
          
          <List dense>
            {playlists.map((playlist) => (
              <ListItem key={playlist.id} disablePadding>
                <ListItemButton onClick={() => handleAddToExistingPlaylist(playlist)}>
                  <ListItemText
                    primary={playlist.name}
                    secondary={`${playlist.tracks?.length || 0} tracks`}
                  />
                  <ListItemSecondaryAction>
                    <Chip 
                      label={playlist.is_public ? 'Public' : 'Private'} 
                      size="small" 
                      variant="outlined"
                    />
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlaylistDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Create playlist dialog */}
      <Dialog 
        open={createPlaylistDialogOpen} 
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Playlist</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new playlist starting with "{track.name}"
          </Typography>
          
          <TextField
            autoFocus
            fullWidth
            label="Playlist Name"
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            placeholder="Enter playlist name"
            onKeyPress={(e) => e.key === 'Enter' && handleCreatePlaylistSubmit()}
            sx={{ mb: 2 }}
          />
          
          <Typography variant="body2" color="success.main">
            Share your playlist to help others discover great music
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button 
            onClick={handleCreatePlaylistSubmit}
            variant="contained"
            disabled={!newPlaylistName.trim()}
            startIcon={<CreateIcon />}
          >
            Create Playlist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase Dialog */}
      <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)}>
        <DialogTitle>
          {isAlbumPurchase ? 'Purchase Album' : 'Purchase Track'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" component="div" gutterBottom>
            {isAlbumPurchase ? (
              <>
                Purchase <strong>{albumName || 'this album'}</strong> for <strong>${purchasePrice.toFixed(2)}</strong>?
                <br />
                <br />
                <Box component="div" sx={{ mt: 1 }}>
                  <Typography variant="body2" component="div">
                    <strong>What you get:</strong>
                  </Typography>
                  <Box component="ul" sx={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                    <li>Permanent ownership of all {trackCount} track{trackCount !== 1 ? 's' : ''} in the album</li>
                    <li>Direct download of all tracks to your device</li>
                    <li>Free streaming on our platform (purchased songs can be streamed for free)</li>
                  </Box>
                  <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>Pricing:</strong> ${purchasePrice.toFixed(2)} ({trackCount} tracks × $2.00 per track)
                  </Typography>
                  <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 1 }}>
                    Note: Purchased songs allow for free streaming within the app. Usage is still monitored to ensure proper tracking.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                Purchase <strong>{track?.name || 'this track'}</strong> for <strong>${purchasePrice.toFixed(2)}</strong>?
                <br />
                <br />
                <Box component="div" sx={{ mt: 1 }}>
                  <Typography variant="body2" component="div">
                    <strong>What you get:</strong>
                  </Typography>
                  <Box component="ul" sx={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                    <li>Permanent ownership of the track</li>
                    <li>Direct download to your device</li>
                    <li>Free streaming on our platform (purchased songs can be streamed for free)</li>
                  </Box>
                  <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 1 }}>
                    Note: Purchased songs allow for free streaming within the app. Usage is still monitored to ensure proper tracking.
                  </Typography>
                </Box>
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPurchaseDialogOpen(false)} 
            disabled={albumPurchaseMutation.isLoading || trackPurchaseMutation.isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePurchaseConfirm} 
            variant="contained" 
            disabled={albumPurchaseMutation.isLoading || trackPurchaseMutation.isLoading}
            startIcon={(albumPurchaseMutation.isLoading || trackPurchaseMutation.isLoading) ? <CircularProgress size={16} /> : <ShoppingCartIcon />}
          >
            {(albumPurchaseMutation.isLoading || trackPurchaseMutation.isLoading) ? 'Purchasing...' : `Purchase $${purchasePrice.toFixed(2)}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Insufficient Credit Dialog */}
      <Dialog open={insufficientCreditDialogOpen} onClose={() => setInsufficientCreditDialogOpen(false)}>
        <DialogTitle>Insufficient Credit</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You don't have enough credit to make this purchase.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Required:</strong> ${purchasePrice.toFixed(2)}
            <br />
            <strong>Current Balance:</strong> ${creditBalance.toFixed(2)}
            <br />
            <strong>Needed:</strong> ${(purchasePrice - creditBalance).toFixed(2)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Please add credit to your account to complete this purchase.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsufficientCreditDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setInsufficientCreditDialogOpen(false);
              setAddCreditDialogOpen(true);
            }}
            variant="contained"
          >
            Add Credit
          </Button>
        </DialogActions>
      </Dialog>

      {saveFileOpen && <SaveFileDialog open onClose={() => setSaveFileOpen(false)} track={track} />}

      {/* Add Credit Dialog */}
      <StripeProvider>
        <DepositDialog 
          open={addCreditDialogOpen} 
          onClose={async () => {
            setAddCreditDialogOpen(false);
            // Refresh credit balance after adding credit
            await refetchCreditBalance();
            // If user now has sufficient credit, show purchase dialog
            const updatedStatus = await refetchCreditBalance();
            if (updatedStatus?.data?.credit_balance >= purchasePrice) {
              setPurchaseDialogOpen(true);
            }
          }} 
        />
      </StripeProvider>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TrackActionsDropdown;
